import React, { useEffect, useState, useRef } from "react";
import styles from "./GroupMessages.module.css";

export enum MessageType {
  LOADING = "loading",
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
  SUCCESS = "success",
}

export interface Message {
  id: string;
  type: MessageType;
  content: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  autoHide?: boolean;
  hideAfter?: number;
}

interface GroupMessagesProps {
  messages: Message[];
  isExiting?: boolean;
  onExited?: () => void;
}

const GroupMessages: React.FC<GroupMessagesProps> = ({
  messages,
  isExiting = false,
  onExited,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Enhanced auto-hide effect for auto-hiding messages
  useEffect(() => {
    // Clean up timeouts when component unmounts
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // Set up auto-hide timers for messages that need it
  useEffect(() => {
    // Clear existing timers when messages change
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    if (messages.length > 0) {
      setIsVisible(true);

      // Set up auto-hide timers for messages that need it
      messages.forEach((message) => {
        if (message.autoHide && message.hideAfter) {
          const timer = setTimeout(() => {
            if (onExited) {
              onExited(); // Signal parent to clean up the message
            }
          }, message.hideAfter);

          timeoutsRef.current.push(timer);
        }
      });
    }
  }, [messages, onExited]);

  // Set visibility based on messages
  useEffect(() => {
    if (messages.length > 0 && !isVisible) {
      setIsVisible(true);
    } else if (messages.length === 0 && isVisible && !isAnimating) {
      setIsVisible(false);
    }
  }, [messages, isVisible, isAnimating]);

  // Handle exiting animation
  useEffect(() => {
    if (isExiting && isVisible) {
      setIsAnimating(true);

      // Call onExited after animation completes
      const timer = setTimeout(() => {
        if (onExited) {
          onExited();
        }
        setIsAnimating(false);
      }, 300); // Match CSS transition duration

      timeoutsRef.current.push(timer);
    }
  }, [isExiting, isVisible, onExited]);

  // Return null when there are no messages and not in exiting state
  if (messages.length === 0 && !isExiting) {
    return null;
  }

  const getMessageIcon = (type: MessageType) => {
    switch (type) {
      case MessageType.LOADING:
        return <span className={styles.spinnerIcon}>⟳</span>;
      case MessageType.ERROR:
        return <span className={styles.errorIcon}>⚠️</span>;
      case MessageType.WARNING:
        return <span className={styles.warningIcon}>⚠️</span>;
      case MessageType.SUCCESS:
        return <span className={styles.successIcon}>✓</span>;
      case MessageType.INFO:
      default:
        return <span className={styles.infoIcon}>ℹ️</span>;
    }
  };

  return (
    <div
      className={`${styles.container} ${isVisible ? styles.visible : ""} ${
        isExiting ? styles.exiting : ""
      }`}
      role="status"
      aria-live="polite"
      onTransitionEnd={() => {
        if (isExiting && onExited) {
          onExited();
        }
      }}
    >
      {messages.map((message) => (
        <div
          key={message.id}
          className={`${styles.message} ${styles[message.type]}`}
        >
          <div className={styles.messageContent}>
            {message.icon || getMessageIcon(message.type)}
            <span>{message.content}</span>
          </div>
          {message.actions && (
            <div className={styles.messageActions}>{message.actions}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default GroupMessages;
